import LockIcon from '@mui/icons-material/Lock';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import VerifiedIcon from '@mui/icons-material/Verified';
import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import Testimonials from '@/components/Testimonials';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const testimonials = () => {
  const { ref, inView } = useInView({
    triggerOnce: false,
    threshold: 0.05,
    rootMargin: '0px',
  });

  const [scale, setScale] = useState(1.08);

  useEffect(() => {
    const handleScroll = () => {
      const newScale = Math.max(1, 1.1 - window.scrollY / 2000);
      setScale(newScale);
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <AppLayout
      meta={
        <Meta
          title="TaxZerone Reviews: Real Users, Ratings & Feedback"
          description="Explore TaxZerone customer reviews and ratings. Discover the No. 1 e-filing platform in US for fast IRS filing, secure tools, trusted support, & real feedback."
          keywords=""
          canonical="user-testimonials/"
        />
      }
    >
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          py: { xs: 8, md: 10 },
          background: `
          radial-gradient(circle at top right, rgba(17,85,204,0.08), transparent 30%),
          radial-gradient(circle at bottom left, rgba(61,183,120,0.08), transparent 35%),
          linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)
        `,
        }}
      >
        <Box
          sx={{
            transform: `translateZ(0) scale(${scale})`,
            transformOrigin: 'center center',
            transition: 'transform 0.8s cubic-bezier(0.22, 1, 0.36, 1)',
            willChange: 'transform',
            backfaceVisibility: 'hidden',
          }}
        >
          {/* Background Text */}
          <Typography
            aria-hidden="true"
            sx={{
              display: { xs: 'none', md: 'block' },
              position: 'absolute',
              top: '28%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: { md: 180, lg: 220 },
              fontWeight: 900,
              letterSpacing: 10,
              color: 'rgba(17,85,204,0.04)',
              userSelect: 'none',
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            REVIEWS
          </Typography>

          {/* Decorative Glow */}
          <Box
            sx={{
              position: 'absolute',
              top: -120,
              right: -80,
              width: 280,
              height: 280,
              borderRadius: '50%',
              background: 'rgba(17,85,204,0.08)',
              filter: 'blur(80px)',
            }}
          />

          <Box
            sx={{
              position: 'absolute',
              bottom: -100,
              left: -80,
              width: 240,
              height: 240,
              borderRadius: '50%',
              background: 'rgba(61,183,120,0.08)',
              filter: 'blur(80px)',
            }}
          />

          <Container maxWidth="lg">
            <Box
              sx={{
                textAlign: 'center',
                position: 'relative',
                zIndex: 2,
              }}
            >
              {/* <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 2,
                  py: 0.8,
                  borderRadius: '999px',
                  bgcolor: '#eef5ff',
                  border: '1px solid #d6e5ff',
                  color: '#1155cc',
                  fontWeight: 700,
                  fontSize: 14,
                  mb: 3,
                }}
              >
                <Box component="span" aria-hidden="true">
                  ⭐
                </Box>
                Rated &amp; Trusted Across the U.S.
              </Box> */}

              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: 32, md: 48 },
                  fontWeight: 800,
                  color: '#0f172a',
                  lineHeight: 1.2,
                  mb: 3,
                }}
              >
                Trusted by{' '}
                <Box component="span" sx={{ color: '#1da94a' }}>
                  Thousands of Businesses,
                </Box>
                <br />
                CPAs &amp; Filers Across the U.S.
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 17, md: 20 },
                  color: '#5b6475',
                  lineHeight: 1.8,
                  maxWidth: 820,
                  mx: 'auto',
                  mb: 5,
                }}
              >
                See why users consistently rate TaxZerone as a fast, secure, and
                easy-to-use IRS e-filing platform. From small businesses to tax
                professionals, our customers share real experiences about
                accuracy, support, and seamless filing.
              </Typography>
              <Button
                variant="contained"
                className="btn-primary"
                href="#customers"
                sx={{
                  mr: { xs: 0, md: 4 },
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
              >
                Explore All Reviews
              </Button>
            </Box>
          </Container>
        </Box>
        <Container
          ref={ref}
          maxWidth="lg"
          sx={{
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(60px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',

            '@keyframes fadeUp': {
              '0%': {
                opacity: 0,
                transform: 'translateY(30px)',
              },
              '100%': {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }}
        >
          <Box
            sx={{
              mt: { xs: 6, md: 8 },
              border: '1px solid #eef2f7',
              borderRadius: '20px',
              boxShadow: '0 20px 50px rgba(17,24,39,0.06)',
              px: { xs: 3, md: 5 },
              py: { xs: 4, md: 5 },
              background: '#ffffff',
            }}
          >
            <Grid container spacing={{ xs: 4, md: 2 }} alignItems="center">
              {/* Overall Rating */}
              <Grid size={{ xs: 12, sm: 4, md: 2.5 }}>
                <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
                  <Typography
                    sx={{
                      fontSize: 48,
                      fontWeight: 800,
                      color: '#0f172a',
                      lineHeight: 1,
                    }}
                  >
                    4.9
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: { xs: 'center', md: 'center' },
                      my: 1,
                      gap: 0.3,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ fontSize: 20, color: '#f5a623' }}
                    >
                      ★
                    </Box>
                    <Box
                      component="span"
                      sx={{ fontSize: 20, color: '#f5a623' }}
                    >
                      ★
                    </Box>
                    <Box
                      component="span"
                      sx={{ fontSize: 20, color: '#f5a623' }}
                    >
                      ★
                    </Box>
                    <Box
                      component="span"
                      sx={{ fontSize: 20, color: '#f5a623' }}
                    >
                      ★
                    </Box>
                    <Box
                      component="span"
                      sx={{ fontSize: 20, color: '#f5a623' }}
                    >
                      ★
                    </Box>
                  </Box>
                  <Typography
                    sx={{ fontWeight: 700, color: '#0f172a', fontSize: 15 }}
                  >
                    Overall Rating
                  </Typography>
                  <Typography sx={{ color: '#94a3b8', fontSize: 13 }}>
                    Based on verified reviews
                  </Typography>
                </Box>
              </Grid>

              <Divider
                orientation="vertical"
                flexItem
                sx={{ display: { xs: 'none', sm: 'block' } }}
              />

              {/* Verified Reviews */}
              <Grid size={{ xs: 12, sm: 4, md: 2.5 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      bgcolor: '#1155cc',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 1.5,
                    }}
                  >
                    <VerifiedIcon sx={{ color: '#fff', fontSize: 24 }} />
                  </Box>
                  <Typography
                    sx={{ fontSize: 32, fontWeight: 800, color: '#0f172a' }}
                  >
                    22,587
                  </Typography>
                  <Typography
                    sx={{ fontWeight: 700, color: '#0f172a', fontSize: 15 }}
                  >
                    Verified Reviews
                  </Typography>
                  <Typography sx={{ color: '#94a3b8', fontSize: 13 }}>
                    Real feedback from verified customers
                  </Typography>
                </Box>
              </Grid>

              <Divider
                orientation="vertical"
                flexItem
                sx={{ display: { xs: 'none', sm: 'block' } }}
              />

              {/* Would Recommend */}
              <Grid size={{ xs: 12, sm: 4, md: 2.5 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      bgcolor: '#3db778',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 1.5,
                    }}
                  >
                    <ThumbUpIcon sx={{ color: '#fff', fontSize: 22 }} />
                  </Box>
                  <Typography
                    sx={{ fontSize: 32, fontWeight: 800, color: '#0f172a' }}
                  >
                    98%
                  </Typography>
                  <Typography
                    sx={{ fontWeight: 700, color: '#0f172a', fontSize: 15 }}
                  >
                    Would Recommend Us
                  </Typography>
                  <Typography sx={{ color: '#94a3b8', fontSize: 13 }}>
                    Customers recommend TaxZerone to others
                  </Typography>
                </Box>
              </Grid>

              <Divider
                orientation="vertical"
                flexItem
                sx={{ display: { xs: 'none', md: 'block' } }}
              />

              {/* Rating Breakdown */}
              <Grid size={{ xs: 12, md: 3.5 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {/* 5 star */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: '#0f172a',
                        width: 10,
                      }}
                    >
                      5
                    </Typography>
                    <Box
                      component="span"
                      sx={{ fontSize: 13, color: '#f5a623' }}
                    >
                      ★
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        height: 6,
                        borderRadius: 999,
                        bgcolor: '#eef2f7',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          width: '91%',
                          height: '100%',
                          bgcolor: '#1155cc',
                          borderRadius: 999,
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: '#94a3b8',
                        width: 32,
                        textAlign: 'right',
                      }}
                    >
                      91%
                    </Typography>
                  </Box>

                  {/* 4 star */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: '#0f172a',
                        width: 10,
                      }}
                    >
                      4
                    </Typography>
                    <Box
                      component="span"
                      sx={{ fontSize: 13, color: '#f5a623' }}
                    >
                      ★
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        height: 6,
                        borderRadius: 999,
                        bgcolor: '#eef2f7',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          width: '6%',
                          height: '100%',
                          bgcolor: '#1155cc',
                          borderRadius: 999,
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: '#94a3b8',
                        width: 32,
                        textAlign: 'right',
                      }}
                    >
                      6%
                    </Typography>
                  </Box>

                  {/* 3 star */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: '#0f172a',
                        width: 10,
                      }}
                    >
                      3
                    </Typography>
                    <Box
                      component="span"
                      sx={{ fontSize: 13, color: '#f5a623' }}
                    >
                      ★
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        height: 6,
                        borderRadius: 999,
                        bgcolor: '#eef2f7',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          width: '2%',
                          height: '100%',
                          bgcolor: '#1155cc',
                          borderRadius: 999,
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: '#94a3b8',
                        width: 32,
                        textAlign: 'right',
                      }}
                    >
                      2%
                    </Typography>
                  </Box>

                  {/* 2 star */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: '#0f172a',
                        width: 10,
                      }}
                    >
                      2
                    </Typography>
                    <Box
                      component="span"
                      sx={{ fontSize: 13, color: '#f5a623' }}
                    >
                      ★
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        height: 6,
                        borderRadius: 999,
                        bgcolor: '#eef2f7',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          width: '0.7%',
                          height: '100%',
                          bgcolor: '#1155cc',
                          borderRadius: 999,
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: '#94a3b8',
                        width: 32,
                        textAlign: 'right',
                      }}
                    >
                      0.7%
                    </Typography>
                  </Box>

                  {/* 1 star */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: '#0f172a',
                        width: 10,
                      }}
                    >
                      1
                    </Typography>
                    <Box
                      component="span"
                      sx={{ fontSize: 13, color: '#f5a623' }}
                    >
                      ★
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        height: 6,
                        borderRadius: 999,
                        bgcolor: '#eef2f7',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          width: '0.3%',
                          height: '100%',
                          bgcolor: '#1155cc',
                          borderRadius: 999,
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: '#94a3b8',
                        width: 32,
                        textAlign: 'right',
                      }}
                    >
                      0.3%
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: { xs: 3, md: 4 } }} />

            {/* Trust strip */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: { xs: 2, sm: 5 },
                bgcolor: '#f6f9ff',
                borderRadius: '14px',
                py: 2,
                px: 3,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VerifiedIcon sx={{ color: '#1155cc', fontSize: 20 }} />
                <Typography
                  sx={{ fontSize: 14, fontWeight: 600, color: '#1155cc' }}
                >
                  All reviews are from verified customers
                </Typography>
              </Box>
              <Box
                id="customers"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <LockIcon sx={{ color: '#1155cc', fontSize: 18 }} />
                <Typography
                  sx={{ fontSize: 14, fontWeight: 600, color: '#1155cc' }}
                >
                  Your data is safe and secure with us
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ mb: 5, mt: { xs: 2, md: 2 } }}>
          {' '}
          <Grid
            container
            spacing={2}
            m={2}
            mt={2}
            pb={4}
            className="MuiGrid-root-width"
            justifyContent="center"
          >
            <Grid size={{ xs: 12, sm: 12, md: 12 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 0,
                  lineHeight: 1.4,
                  textAlign: 'center',
                }}
              >
                What Our Customers Love Most
              </Typography>
            </Grid>
          </Grid>
          <Testimonials />
        </Container>
      </Grid>

      <Box
        sx={{
          backgroundColor: '#1da94a',
          py: { lg: 8, md: 8, sm: 4, xs: 4 },
          mx: '0 !important',
          width: '100% !important',
          mb: 0,
          p: 2,
          textAlign: 'center',
        }}
        m={2}
        mt={0}
        className="curve-bg"
      >
        <Typography
          variant="h2"
          color="white"
          sx={{
            fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
            fontWeight: 600,
            mb: 3,
          }}
        >
          Ready to File Your IRS Forms Today?
        </Typography>

        <Container maxWidth="md" sx={{ px: 0 }}>
          <Typography
            variant="h3"
            sx={{
              fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
              fontWeight: 400,
              color: '#f3f3f3',
              mb: 4,
              lineHeight: 1.6,
            }}
          >
            Join <strong>50,000+</strong> businesses, individuals, and
            nonprofits who trust TaxZerone for fast, accurate, IRS-authorized
            e-filing.
          </Typography>
        </Container>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Button
            variant="contained"
            className="btn-secondary-white"
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=usertestimonials`}
            sx={{
              color: '#444',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: '#213e99 !important',
                color: '#fff',
                transform: 'scale(1.08)',
              },
            }}
          >
            Sign Up for FREE
          </Button>

          <Button
            variant="outlined"
            href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}?ref=usertestimonials`}
            sx={{
              borderColor: '#fff',
              color: '#fff',
              px: 4,
              py: 1.2,
              borderRadius: '16px',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: '#213e99',
                borderColor: '#213e99',
                color: '#fff',
                borderRadius: '16px',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
              },
            }}
          >
            View All Forms
          </Button>
        </Box>
      </Box>
    </AppLayout>
  );
};

export default testimonials;
